import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Cpu, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDiagnosticContext } from '../context/DiagnosticContext';
import styles from './Hero.module.css';

export const Hero = () => {
  const { user } = useAuth();
  const { resetDiagnostic } = useDiagnosticContext();
  const navigate = useNavigate();

  const handleStart = (e: React.MouseEvent) => {
    e.preventDefault();
    resetDiagnostic();
    navigate('/diagnostic');
  };

  return (
    <section className={styles.hero}>
      <div className="wrap">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className={styles.badge}>
            <span className={styles.pulse}></span>
            AI-Powered Diagnostics
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Find out why your<br />Windows PC is <span>slow</span>.
        </motion.h1>

        <motion.p
          className={styles.sub}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Run professional-grade diagnostics in minutes. Get an AI-assisted health report that identifies root causes and provides safe, manual fix instructions.
        </motion.p>

        <motion.div
          className={styles.actions}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <button onClick={handleStart} className="btn btn-primary">
            Start Free Scan <Zap size={18} className={styles.zapIcon} />
          </button>


          {user ? (
            <Link to="/dashboard" className="btn btn-secondary">
              Go to Dashboard <ArrowRight size={18} />
            </Link>
          ) : (
            <p className={styles.signInHint}>
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          )}
        </motion.div>

        <motion.div
          className={styles.safetyLine}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          No installation &bull; Read-only &bull; No personal documents, passwords, photos, or browsing history are collected.
        </motion.div>

        <motion.div
          className={styles.trustIndicators}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <div className={styles.indicator}>
            <Download size={16} /> <span>One Scanner</span>
          </div>
          <div className={styles.indicator}>
            <Cpu size={16} /> <span>AI-Powered</span>
          </div>
          <div className={styles.indicator}>
            <Shield size={16} /> <span>Read-Only Analysis</span>
          </div>
          <div className={styles.indicator}>
            <Zap size={16} /> <span>Privacy Focused</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
